import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Container, Paper, Typography, Button } from '@mui/material';
import OutdoorGrillRounded from '@mui/icons-material/OutdoorGrillRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import { useOrder } from '../../state/OrderContext';
import { useMenu } from '../../state/MenuContext';

const KitchenScreen: React.FC = () => {
  const { t } = useTranslation();
  const { state, markDelivered } = useOrder();
  const { getMenuItem } = useMenu();
  const [now, setNow] = useState<number>(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  const timeAgo = (ts: number | undefined): string => {
    if (!ts) return '';
    const minutes = Math.floor((now - ts) / 60000);
    if (minutes < 1) return t('kitchen.justNow');
    return t('kitchen.minutesAgo', { count: minutes });
  };

  const kitchenOrders = useMemo(
    () =>
      Object.values(state.tables)
        .map((order) => ({
          order,
          pending: order.items.filter((item) => item.sent && !item.delivered),
        }))
        .filter((entry) => entry.pending.length > 0)
        .sort((a, b) => (a.order.lastSentAt ?? 0) - (b.order.lastSentAt ?? 0)),
    [state.tables]
  );

  return (
    <Container maxWidth="sm" sx={{ py: 2 }}>
      {kitchenOrders.length === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 6, color: '#BDBDBD' }}>
          <OutdoorGrillRounded sx={{ fontSize: 72, mb: 1.5 }} />
          <Typography sx={{ fontWeight: 600, fontSize: '1.1rem', mb: 0.5, color: '#BDBDBD' }}>{t('kitchen.allDone')}</Typography>
          <Typography sx={{ fontSize: '0.85rem' }}>{t('kitchen.noOpenOrders')}</Typography>
        </Box>
      ) : (
        kitchenOrders.map(({ order, pending }) => (
          <Paper key={order.tableNumber} elevation={0} sx={{ mb: 1.5, overflow: 'hidden', border: '1px solid #F5F5F5' }}>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                backgroundColor: '#FFF5F5',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #FFECEC',
              }}
            >
              <Box>
                <Typography component="span" sx={{ fontWeight: 800, fontSize: '1.25rem', color: 'primary.main' }}>
                  {order.tableNumber}
                </Typography>
                <Typography component="span" sx={{ fontSize: '0.75rem', color: '#BDBDBD', ml: 1.25 }}>
                  {timeAgo(order.lastSentAt)}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                startIcon={<CheckRounded />}
                onClick={() => markDelivered(order.tableNumber)}
                sx={{ backgroundColor: '#43A047' }}
              >
                {t('kitchen.delivered')}
              </Button>
            </Box>
            {pending.map((line) => {
              const menuItem = getMenuItem(line.menuItemId);
              if (!menuItem) return null;
              return (
                <Box
                  key={line.menuItemId}
                  sx={{ px: 2, py: 1.375, display: 'flex', alignItems: 'center', gap: 1.25, borderBottom: '1px solid #F9F9F9' }}
                >
                  <Box sx={{ backgroundColor: '#FFECEC', color: 'primary.main', borderRadius: '8px', fontWeight: 800, fontSize: '0.8rem', px: 1.25, py: 0.375 }}>
                    {line.quantity}×
                  </Box>
                  <Typography sx={{ fontWeight: 500, fontSize: '0.95rem', color: '#212121' }}>{menuItem.name}</Typography>
                </Box>
              );
            })}
          </Paper>
        ))
      )}
    </Container>
  );
};

export default KitchenScreen;
